/**
 * Transcript parsing utilities for SessionPanel
 */

import type { UpdateEvent } from "../../../types/domain";

// ============================================
// Types
// ============================================

type Row = {
  order: number;
  seqHint: number;
  speaker?: string;
  text: string;
  start_sec?: number;
  end_sec?: number;
  chunk_index?: number;
};

// ============================================
// Parsing functions
// ============================================

/**
 * Parse a transcript text (JSONL format) into UpdateEvent array
 */
export function parseTranscriptToEvents(text: string | undefined | null): UpdateEvent[] {
  if (!text) return [];
  const lines = text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  const rows: Row[] = [];
  let hint = 0;

  for (const line of lines) {
    if (!(line.startsWith("{") && line.endsWith("}"))) continue;
    let obj: any;
    try {
      obj = JSON.parse(line);
    } catch {
      continue;
    }

    const start_ms = typeof obj?.start_ms === "number" ? obj.start_ms : undefined;
    const end_ms = typeof obj?.end_ms === "number" ? obj.end_ms : undefined;
    const chunkIdx = typeof obj?.chunk_index === "number" ? obj.chunk_index : undefined;

    const order =
      typeof start_ms === "number"
        ? start_ms
        : typeof chunkIdx === "number"
        ? chunkIdx * 1e6
        : Number.MAX_SAFE_INTEGER;

    if (Array.isArray(obj?.turns) && obj.turns.length) {
      for (const turn of obj.turns) {
        const t = (turn?.text ?? "").trim();
        if (!t) continue;
        // Use turn's timing if available, fall back to chunk timing
        const turnStartSec = typeof turn?.start_sec === "number" ? turn.start_sec : (start_ms != null ? start_ms / 1000 : undefined);
        const turnEndSec = typeof turn?.end_sec === "number" ? turn.end_sec : (end_ms != null ? end_ms / 1000 : undefined);
        rows.push({
          order,
          seqHint: hint++,
          speaker: turn?.speaker,
          text: t,
          start_sec: turnStartSec,
          end_sec: turnEndSec,
          chunk_index: chunkIdx,
        });
      }
    } else if (typeof obj?.raw_transcription === "string" && obj.raw_transcription.trim()) {
      rows.push({
        order,
        seqHint: hint++,
        text: obj.raw_transcription.trim(),
        start_sec: start_ms != null ? start_ms / 1000 : undefined,
        end_sec: end_ms != null ? end_ms / 1000 : undefined,
        chunk_index: chunkIdx,
      });
    }
  }

  rows.sort((a, b) => a.order - b.order || a.seqHint - b.seqHint);
  let seq = 1;
  return rows.map(
    (r) =>
      ({
        seq: seq++,
        type: "text",
        text: r.text,
        speaker: r.speaker,
        start_sec: r.start_sec,
        end_sec: r.end_sec,
        chunk_index: r.chunk_index,
      } as any)
  );
}

// ============================================
// Live transcript decoding
// ============================================

/**
 * Extract items array from various response formats
 */
export function extractItems(data: any): any[] {
  if (!data) return [];
  if (Array.isArray(data.events)) return data.events;
  if (Array.isArray(data.bubbles)) return data.bubbles;
  if (Array.isArray(data.newTurns)) return data.newTurns;
  if (Array.isArray(data.new_turns)) return data.new_turns;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.turns)) return data.turns;
  return [];
}

function asNumberMaybe(x: any): number | undefined {
  return typeof x === "number" && Number.isFinite(x) ? x : undefined;
}

/**
 * Convert raw bubble/turn data to UpdateEvent
 */
export function toUIEvent(b: any): UpdateEvent | null {
  if (!b) return null;

  const seq =
    typeof b.seq === "number"
      ? b.seq
      : typeof b.chunk_index === "number" && typeof b.local_index === "number"
      ? b.chunk_index * 1000 + b.local_index
      : undefined;

  const text = (b.text ?? b.utterance ?? b.content ?? "").toString().trim();
  if (!text) return null;

  const speaker = (b.speaker ?? b.role ?? "").toString();

  const start_sec =
    asNumberMaybe(b.start_sec) ?? (typeof b.start_ms === "number" ? b.start_ms / 1000 : undefined);

  const end_sec =
    asNumberMaybe(b.end_sec) ?? (typeof b.end_ms === "number" ? b.end_ms / 1000 : undefined);

  const op = (b.op ?? "").toString().trim();
  const turn_id = (b.turn_id ?? "").toString().trim();
  const is_final = typeof b.is_final === "boolean" ? b.is_final : undefined;

  return {
    seq: seq ?? 0,
    type: "text" as any,
    text,
    speaker,
    start_sec,
    end_sec,
    ...(op ? { op } : {}),
    ...(turn_id ? { turn_id } : {}),
    ...(is_final !== undefined ? { is_final } : {}),
  } as any;
}

/**
 * Merge incoming events with existing events, handling updates
 */
export function mergeStream(prev: UpdateEvent[], incoming: UpdateEvent[]): UpdateEvent[] {
  if (!incoming.length) return prev;

  const out: UpdateEvent[] = [...prev];

  const idxBySeq = new Map<number, number>();
  for (let i = 0; i < out.length; i++) {
    const s = (out[i] as any).seq;
    if (typeof s === "number" && Number.isFinite(s) && s > 0) idxBySeq.set(s, i);
  }

  for (const e of incoming) {
    const s = (e as any).seq;
    if (!(typeof s === "number" && Number.isFinite(s) && s > 0)) continue;

    const op = ((e as any).op ?? "").toString().trim().toLowerCase();

    if (op === "update") {
      const idx = idxBySeq.get(s);
      if (idx != null) {
        out[idx] = { ...(out[idx] as any), ...(e as any), seq: s } as any;
      } else {
        out.push(e);
        idxBySeq.set(s, out.length - 1);
      }
      continue;
    }

    if (idxBySeq.has(s)) continue;
    out.push(e);
    idxBySeq.set(s, out.length - 1);
  }

  out.sort((a: any, b: any) => (a.seq ?? 0) - (b.seq ?? 0));
  return out;
}
